import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Grid3X3, List, Shuffle, Clock, CheckCircle, Loader2, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import PostPromptModal from '@/components/bulletins/PostPromptModal';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLoops, useLoopActions } from '@/hooks/use-loops';
import LoopCard from '@/components/loops/LoopCard';
import LoopFeed from '@/components/loops/LoopFeed';
import LoopSwiper from '@/components/loops/LoopSwiper';
// Incrementally adding transitions back
import TransitionLoopFeed from '@/components/transitions/TransitionLoopFeed';
import ExpandedLoopPost from '@/components/loops/ExpandedLoopPost';
import usePostTransition from '@/hooks/use-post-transition';
import { TransitionProvider } from '@/components/transitions/TransitionContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import MetaTags from '@/components/meta/MetaTags';
import PageLayout from '@/components/layout/PageLayout';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { useReblogAnnotations, useReblogActions } from '@/hooks/use-reblogs';
import TrailLensDialog from '@/components/loops/TrailLensDialog';
import { useBulletinForm } from '@/hooks/use-bulletin-form';
import LoopUploadProgress from '@/components/loops/LoopUploadProgress';

type ViewMode = 'card' | 'feed';
type OrderType = 'chronological' | 'mixed';

const LoopsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode: routeMode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { isAdmin } = useUserRoles();
  
  // Use bulletin form for consistent posting logic
  const bulletinForm = useBulletinForm(false, () => {
    setShowPostPrompt(false);
  }, true); // Pass isLoopPost=true to prevent duplicate toast notifications
  
  const [viewMode, setViewMode] = useState<ViewMode>(
    searchParams.get('view') === 'card' ? 'card' : 'feed'
  );
  const orderType: OrderType = 'mixed';
  const [engagedLoops, setEngagedLoops] = useState<Set<string>>(new Set());
  const [currentSwiperIndex, setCurrentSwiperIndex] = useState(0);
  const [showPostPrompt, setShowPostPrompt] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [showPostSuccess, setShowPostSuccess] = useState(false);
  const postSuccessTimeoutRef = useRef<NodeJS.Timeout>();
  // Infinite scroll sentinel for feed view
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [showModerationDebug, setShowModerationDebug] = useState(false);
  
  // Add post transition functionality with in-feed expansion
  const { activeLoop, isPostOpen, expandedPostId, openPost, closePost } = usePostTransition();
  
  // State for TrailLens dialog
  const [trailLensOpen, setTrailLensOpen] = useState(false);
  const [selectedLoopId, setSelectedLoopId] = useState<string | null>(null);

  // Hook for loop actions
  const { likeLoop, unlikeLoop, deleteLoop } = useLoopActions();

  // Hook for reblog actions
  const { reblog, quote, unreloop, hasReblogged, getReblogDetails } = useReblogActions();

  // Fetch loops data
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error
  } = useLoops(orderType);
  
  const loops = useMemo(() => 
    data?.pages.flatMap(page => page) || [], 
    [data]
  );

  // Infinite scroll: observe sentinel to fetch next page automatically (feed view)
  useEffect(() => {
    if (viewMode !== 'feed') return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, { rootMargin: '600px 0px 600px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [viewMode, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle URL based navigation after loops are loaded
  useEffect(() => {
    const postId = searchParams.get('post');
    
    if (postId) {
      // Only open if it's a different post than currently expanded
      if (expandedPostId !== postId) {
        // Try to find the loop in loaded data
        const targetLoop = loops.find(l => l.id === postId);
        if (targetLoop) {
          // Open this post
          openPost(targetLoop);
        } else if (loops.length > 0 && !isLoading) {
          // Loop not found in current page - fetch it from database
          console.log('Loop not found in current page, fetching:', postId);
          // Fetch the specific loop
          import('@/integrations/supabase/client').then(({ supabase }) => {
            supabase
              .from('loops')
              .select('*')
              .eq('id', postId)
              .single()
              .then(({ data, error }) => {
                if (data && !error) {
                  // Fetch the user profile for this loop
                  supabase
                    .from('profiles')
                    .select('id, username, display_name, profile_image_url, tag_code, tag_emote, is_admin, is_premium')
                    .eq('id', data.user_id)
                    .single()
                    .then(({ data: profile }) => {
                      const enrichedLoop = { ...data, user: profile || { username: 'unknown', display_name: 'Unknown' } };
                      openPost(enrichedLoop);
                    });
                } else {
                  console.error('Failed to fetch loop:', error);
                }
              });
          });
        }
      }
    } else if (expandedPostId) {
      // If no post in URL but we have an expanded post, close it
      closePost();
    }
  }, [loops, searchParams, expandedPostId, openPost, closePost, isLoading]);

  // Fetch reblog annotations for visible loops
  const postIds = useMemo(() => loops.map(l => l.id), [loops]);
  const { data: annotationsMap } = useReblogAnnotations(postIds);
  
  // Update URL params when view mode or order changes
  useEffect(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (viewMode !== 'card') {
        params.set('view', viewMode);
      } else {
        params.delete('view');
      }
      if (showModerationDebug) {
        params.set('debug', 'moderation');
      } else {
        params.delete('debug');
      }
      return params;
    }, { replace: true });
  }, [viewMode, showModerationDebug, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('debug') === 'moderation' && isAdmin) {
      setShowModerationDebug(true);
    }
  }, [searchParams, isAdmin]);
  
  const handleEngage = useCallback((loopId: string) => {
    setEngagedLoops(prev => new Set([...prev, loopId]));
  }, []);
  
  // Handle post click for transitions
  const handlePostClick = useCallback((loop: any) => {
    console.log('Post clicked:', loop.id);
    openPost(loop);
  }, [openPost]);

  // Handle like action
  const handleLike = useCallback((loop: any) => {
    if (loop.is_liked) {
      unlikeLoop(loop.id);
    } else {
      likeLoop(loop.id);
    }
  }, [likeLoop, unlikeLoop]);
  
  // Handle reblog action
  const handleReblog = useCallback((loop: any) => {
    // Check if already reblogged and toggle
    const isReblogged = hasReblogged(loop.id);
    if (isReblogged) {
      const details = getReblogDetails(loop.id);
      if (details?.reblog_id) {
        unreloop(details.reblog_id);
      }
    } else {
      reblog(loop.id);
      toast({
        description: "Relooped successfully",
      });
    }
  }, [reblog, unreloop, hasReblogged, getReblogDetails, toast]);

  // Handle comment action
  const handleComment = useCallback((loop: any) => {
    // Open the post and focus on comment section
    openPost(loop, true);
  }, [openPost]);

  // Handle quote action
  const handleQuote = useCallback((loop: any) => {
    // Open a prompt to get quote content
    setShowPostPrompt(true);
    
    // Store a reference to the post ID we're quoting
    // We'll include the quote reference in the content when the user submits
    const quotePrefix = `Quoting @${loop.user?.username || 'user'}: "${loop.content?.substring(0, 50)}${loop.content?.length > 50 ? '...' : ''}"

`;
    
    // Pre-populate the form with quote reference
    bulletinForm.setContent(quotePrefix);
    
    toast({
      description: "Add your thoughts to quote this post",
    });
  }, [bulletinForm]);

  // Handle trail view dialog
  const handleViewTrail = useCallback((loop: any) => {
    setSelectedLoopId(loop.id);
    setTrailLensOpen(true);
  }, []);
  
  // Handle post deletion
  const handleDeletePost = useCallback((loop: any) => {
    // Delete the post - backend will verify user permissions
    deleteLoop({
      loopId: loop.id,
      userId: loop.user_id
    });
  }, [deleteLoop]);
  
  // Handle more actions - legacy code now updated to support deletion and trail viewing separately
  const handleMoreActions = useCallback((loop: any) => {
    // By default, just show trail view
    // This is kept for backward compatibility - the component will determine what to do
    setSelectedLoopId(loop.id);
    setTrailLensOpen(true);
  }, []);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, []);

  const handleModerationDebugToggle = useCallback(() => {
    setShowModerationDebug(prev => !prev);
  }, []);
  
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  
  if (isLoading) {
    return <LoadingFallback />;
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
        <p className="text-muted-foreground mb-4">Failed to load loops</p>
        <Button onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }
  
  return (
    <>
      <MetaTags 
        title="Loops - Share Your Moments"
        description="Discover and share moments in our Twitter-like feed. Engage with posts to reveal authors and join conversations."
      />
      <TransitionProvider>
      <ErrorBoundary>
      <PostPromptModal
        open={showPostPrompt}
        title=""
        description=""
        onClose={() => setShowPostPrompt(false)}
        onSubmit={async (
          postCopy: string,
          mediaTitle: string,
          mediaDescription: string,
          image?: File | null,
          isArt?: boolean,
          disabledCommunityIds?: Set<string>
        ) => {
          // Combine title and description for all posts (both text-only and media)
          let content = postCopy || '';
          if (mediaDescription?.trim()) {
            content = content ? `${content}\n\n${mediaDescription}` : mediaDescription;
          }
          
          try {
            // Show posting state
            setIsPosting(true);
            setShowPostPrompt(false); // Close the prompt immediately
            
            // Set the artstream flag based on toggle
            if (isArt !== undefined) {
              bulletinForm.setIsArtstream(isArt);
            }
            
            // Store disabled communities for community extraction
            if (disabledCommunityIds) {
              bulletinForm.setDisabledCommunities(disabledCommunityIds);
            }
            
            // Timeout protection: Ensure posting never hangs forever
            const uploadPromise = bulletinForm.handleSubmit(
              null, 
              undefined, 
              content, 
              image || undefined, 
              undefined, // directLoopsContent
              disabledCommunityIds // overrideDisabledCommunities - critical for respecting toggles
            );
            
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Upload timed out after 5 minutes')), 300000) // 5 minutes for slow connections
            );
            
            // Race between upload and timeout
            const success = await Promise.race([uploadPromise, timeoutPromise]) as boolean;
            
            if (success) {
              // Show success state
              setShowPostSuccess(true);
              
              // Auto-hide success message after 3 seconds
              if (postSuccessTimeoutRef.current) {
                clearTimeout(postSuccessTimeoutRef.current);
              }
              
              postSuccessTimeoutRef.current = setTimeout(() => {
                setShowPostSuccess(false);
              }, 3000);
              
              // We don't need a toast notification here since the bulletin form already shows one
            }
          } catch (error) {
            console.error('Error posting to loops:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast({
              title: "Failed to post",
              description: errorMessage.includes('timed out') 
                ? "Upload timed out after 5 minutes. Please check your connection and try again."
                : "There was an error posting your content. Please try again.",
              variant: "destructive"
            });
          } finally {
            setIsPosting(false);
          }
        }}
      />
      <PageLayout
        showComposer
        hidePostButtons
        mode="all"
        onModeChange={undefined}
        pageTitle="Loops"
        viewModeButtons={
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-lg border border-[#343536] bg-[#272729] p-1">
              <Button
                variant={viewMode === 'card' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handleViewModeChange('card')}
                className="rounded-md"
                aria-label="Card View"
              >
                <Grid3X3 size={16} />
              </Button>
              <Button
                variant={viewMode === 'feed' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handleViewModeChange('feed')}
                className="rounded-md"
                aria-label="Feed View"
              >
                <List size={16} />
              </Button>
            </div>
            {isAdmin && (
              <Button
                variant={showModerationDebug ? 'secondary' : 'ghost'}
                size="sm"
                onClick={handleModerationDebugToggle}
                className="rounded-md flex items-center gap-1"
                aria-label="Toggle moderation debug"
              >
                <Bug size={16} />
                <span className="hidden sm:inline">Debug</span>
              </Button>
            )}
          </div>
        }
        onCreate={async (text: string, file?: File) => {
          // For loops, we allow empty text posts
          const content = (text || '');
          
          // Submit directly with content and image to avoid race condition
          await bulletinForm.handleSubmit(null, undefined, content, file);
        }}
        showRefresh
        onRefresh={() => {}}
        onTagSelect={() => {}}
        onCompose={() => setShowPostPrompt(true)}
        transparentHeader={false}
        hideHeader={false}
      >
        <div className="min-h-screen bg-[#1A1A1B] relative">
          {/* Upload progress banner - non-blocking, shows actual progress */}
          <LoopUploadProgress 
            isUploading={isPosting}
            onDismiss={() => {
              setIsPosting(false);
              setShowPostSuccess(false);
            }}
          />

          {/* Success notification */}
          <AnimatePresence>
            {showPostSuccess && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-500/90 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 max-w-sm w-full"
              >
                <CheckCircle className="h-5 w-5 text-white" />
                <div>
                  <h4 className="font-medium">Loop posted!</h4>
                  <p className="text-sm text-white/90">Your content has been published</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Content */}
          <div className="container max-w-4xl mx-auto px-0 sm:px-4 py-6 text-zinc-100">
            <AnimatePresence mode="wait">
              {viewMode === 'card' ? (
                <motion.div
                  key="card-view"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="h-[calc(100vh-140px)]"
                >
                  <LoopSwiper
                    loops={loops}
                    currentIndex={currentSwiperIndex}
                    onIndexChange={setCurrentSwiperIndex}
                    onEngage={handleEngage}
                    engagedLoops={engagedLoops}
                    annotationsMap={annotationsMap}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="feed-view"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {/* If a post is expanded, only show that post */}
                  {expandedPostId ? (
                    (() => {
                      const expandedLoop = loops.find(loop => loop.id === expandedPostId);
                      if (!expandedLoop) return null;

                      return (
                        <motion.div
                          key={expandedLoop.id}
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          id={`expanded-post-${expandedLoop.id}`}
                          className="mb-4 w-full"
                        >
                          <ExpandedLoopPost
                            loop={expandedLoop}
                            onClose={closePost}
                          />
                        </motion.div>
                      );
                    })()
                  ) : (
                    loops.map((loop) => (
                      <TransitionLoopFeed
                        key={loop.id}
                        loop={loop}
                        onEngage={handleEngage}
                        isEngaged={engagedLoops.has(loop.id) || !!loop.is_liked}
                        annotations={annotationsMap?.[loop.id]}
                        onPostClick={handlePostClick}
                        onLike={handleLike}
                        onReblog={handleReblog}
                        onComment={handleComment}
                        onQuote={handleQuote}
                        onMoreActions={handleViewTrail}
                        showModerationDebug={showModerationDebug && isAdmin}
                      />
                    ))
                  )}
                  
                  {/* Infinite scroll sentinel for feed view */}
                  {viewMode === 'feed' && !expandedPostId && (
                    <div ref={loadMoreRef} className="h-20 flex items-center justify-center">
                      {isFetchingNextPage && (
                        <div className="text-sm text-muted-foreground">Loading more...</div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* Load more trigger for card view */}
          {viewMode === 'card' && hasNextPage && currentSwiperIndex >= loops.length - 3 && (
            <div className="fixed bottom-4 right-4 z-50">
              <Button
                onClick={handleLoadMore}
                disabled={isFetchingNextPage}
                size="sm"
                className="rounded-full shadow-lg bg-[#272729] hover:bg-[#2D2D2F] border border-[#343536] text-[#D7DADC]"
              >
                {isFetchingNextPage ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}  
        </div>

        {/* Expanded post now handled inline within the feed */}

        {/* TrailLens dialog for viewing post history */}
        <TrailLensDialog
          postId={selectedLoopId || ''}
          open={trailLensOpen}
          onOpenChange={setTrailLensOpen}
        />
      </PageLayout>
      </ErrorBoundary>
      </TransitionProvider>
    </>
  );
};

export default LoopsPage;